import * as yup from 'yup';
import React, { memo, useCallback, useState } from 'react';
import { IconSended } from '@app/icons';
import { useUserContext } from '@app/providers';
import { useForm } from '@app/forms';
import { Company, Tender, User } from '@app/models';
import { postReport } from '@app/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Textarea } from 'components/ui/textarea';
import Loader from 'components/Loader/Loader';

interface Props {
  tender?: Tender;
  user?: User;
  company?: Company;
  visible: boolean;
  onClose?: () => void;
}

interface IReport {
  message: string;
}
function ReportModal(props: Props) {
  const { tender, user, company, visible, onClose } = props;
  const [messageSent, onSendMessage] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const { user: author } = useUserContext();

  const [data] = useState(
    company ||
      tender || {
        ...user,
        name: `${user?.firstName} ${user?.middleName} ${user?.lastName}`,
      }
  );

  const schema = yup.object().shape({
    message: yup.string().required(),
  });

  const { errors, onChange, values, validate } = useForm<IReport>({
    message: '',
  });

  const onSubmitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const hasErrors = await validate(schema);

    if (hasErrors) {
      return;
    }

    try {
      const reportData = {
        message: values.message,
        authorId: author.id,
        tenderId: tender && tender.id,
        userId: user && user.id,
        companyId: company && company.id,
      };
      await postReport(reportData);
      setIsPending(true);
      setTimeout(() => {
        setIsPending(false);
      }, 1500);
      onSendMessage(true);
    } catch (error) {}
  };

  const onCloseModal = useCallback(() => {
    onSendMessage(false);
    onChange('', 'message');

    if (!!onClose) {
      onClose();
    }
  }, [onChange, onClose]);

  return (
    <Dialog
      open={visible}
      onOpenChange={(open) => {
        if (!open) onCloseModal();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {!messageSent ? `Пожаловаться на ${data ? data.name : ''}` : ''}
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          {messageSent ? (
            <div className="flex flex-col items-center justify-center gap-4 py-8">
              {isPending ? (
                <Loader />
              ) : (
                <>
                  <span className="text-4xl text-green-500">
                    <IconSended />
                  </span>
                  <div className="text-lg font-semibold">
                    Жалоба отправлена!
                  </div>
                  <Button onClick={onClose}>Понятно</Button>
                </>
              )}
            </div>
          ) : (
            <form onSubmit={onSubmitForm} className="flex flex-col gap-4">
              <label
                className="text-xs text-muted-foreground mb-1"
                htmlFor="report-message"
              >
                Комментарий<span className="text-red-500">*</span>
              </label>
              <Textarea
                id="report-message"
                value={values.message}
                placeholder="Введите суть жалобы"
                name="message"
                required
                onChange={(e) => onChange(e.target.value, 'message')}
                className={errors.message ? 'border-red-500' : ''}
              />
              {errors.message && (
                <div className="text-xs text-red-500">{errors.message}</div>
              )}
              <DialogFooter>
                <Button type="submit">Отправить</Button>
              </DialogFooter>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default memo(ReportModal);
